import {
  SidePanelDisplayStyle,
  type SidePanelMotion,
  SidePanelSide,
} from 'designSystem/components/sidePanelContext'
import { cn } from 'designSystem/lib/utils'
import isNil from 'lodash/isNil'
import type { CSSProperties } from 'react'

export interface SidePanelRootCssProperties extends CSSProperties {
  '--sp-min': string
  '--sp-max'?: string
  '--sp-main-min': string
}

export function sidePanelRootStyle({
  gridTemplateColumns,
  maxWidth,
  minMainContentWidth,
  minWidth,
}: {
  gridTemplateColumns: string
  maxWidth: number | undefined
  minMainContentWidth: number
  minWidth: number
}): SidePanelRootCssProperties {
  return {
    '--sp-min': `${minWidth}px`,
    ...(isNil(maxWidth) ? {} : { '--sp-max': `${maxWidth}px` }),
    '--sp-main-min': `${minMainContentWidth}px`,
    gridTemplateColumns,
    gridTemplateRows: 'minmax(0, 1fr)',
  }
}

function sidePanelOpenTrack({
  maxWidth,
  preferredPanelTrack,
}: {
  maxWidth: number | undefined
  preferredPanelTrack: string
}): string {
  const remaining = 'calc(100% - var(--sp-main-min))'
  const upperBound = isNil(maxWidth)
    ? remaining
    : `min(var(--sp-max), ${remaining})`
  return `clamp(var(--sp-min), ${preferredPanelTrack}, ${upperBound})`
}

export function sidePanelGridTemplateColumns({
  displayStyle,
  expandPanel,
  maxWidth,
  preferredPanelTrack,
  reserveColumn,
  side,
}: {
  displayStyle: SidePanelDisplayStyle
  expandPanel?: boolean
  maxWidth: number | undefined
  preferredPanelTrack: string
  reserveColumn: boolean
  side: SidePanelSide
}): string {
  if (displayStyle !== SidePanelDisplayStyle.Sidebar) {
    return 'minmax(0, 1fr)'
  }
  if (expandPanel && reserveColumn) {
    return side === SidePanelSide.Right
      ? '0px minmax(0, 1fr)'
      : 'minmax(0, 1fr) 0px'
  }
  const panelTrack = sidePanelOpenTrack({ maxWidth, preferredPanelTrack })
  const activePanelTrack = reserveColumn ? panelTrack : '0px'
  return side === SidePanelSide.Right
    ? `minmax(0, 1fr) ${activePanelTrack}`
    : `${activePanelTrack} minmax(0, 1fr)`
}

export function sidePanelMainStyle({
  displayStyle,
  side,
}: {
  displayStyle: SidePanelDisplayStyle
  side: SidePanelSide
}): CSSProperties | undefined {
  if (
    displayStyle === SidePanelDisplayStyle.Sidebar &&
    side === SidePanelSide.Left
  ) {
    return { gridColumn: 2 }
  }
  return undefined
}

export function sidePanelAsideClassName({
  expandPanel = false,
  motion,
  side,
  variant,
}: {
  expandPanel?: boolean
  motion: SidePanelMotion
  side: SidePanelSide
  variant: 'flush' | 'inset'
}): string {
  const smoothMotion = motion === 'smooth'
  const asideSheetClasses = cn(
    'data-[panel-display-style=sheet]:fixed',
    'data-[panel-display-style=sheet]:top-(--cantilever-navbar-height,0px)',
    'data-[panel-display-style=sheet]:bottom-0',
    'data-[panel-display-style=sheet]:w-(--sp-w)',
    'data-[panel-display-style=sheet]:max-w-full',
    'data-[panel-display-style=sheet]:shadow-lg',
    side === SidePanelSide.Right
      ? 'data-[panel-display-style=sheet]:right-0 data-[panel-display-style=sheet]:border-l'
      : 'data-[panel-display-style=sheet]:left-0 data-[panel-display-style=sheet]:border-r'
  )
  const asideSidebarClasses = cn(
    variant === 'flush' &&
      !expandPanel &&
      (side === SidePanelSide.Right
        ? 'data-[panel-display-style=sidebar]:border-l'
        : 'data-[panel-display-style=sidebar]:border-r'),
    'data-[panel-display-style=sidebar]:transition-[opacity,transform]',
    smoothMotion
      ? [
          'data-[panel-display-style=sidebar]:duration-250',
          'data-[panel-display-style=sidebar]:ease-drawer',
          'data-[panel-display-style=sidebar]:motion-reduce:transition-opacity',
          'data-[panel-display-style=sidebar]:motion-reduce:duration-150',
        ]
      : [
          'data-[panel-display-style=sidebar]:duration-300',
          'data-[panel-display-style=sidebar]:ease-out',
          'data-[panel-display-style=sidebar]:motion-reduce:transition-none',
        ],
    'data-[panel-display-style=sidebar]:data-[panel-open=false]:pointer-events-none',
    !smoothMotion &&
      'data-[panel-display-style=sidebar]:data-[panel-open=false]:invisible',
    'data-[panel-display-style=sidebar]:data-[panel-open=false]:opacity-0',
    side === SidePanelSide.Right
      ? smoothMotion
        ? 'data-[panel-display-style=sidebar]:data-[panel-open=false]:motion-safe:translate-x-4'
        : 'data-[panel-display-style=sidebar]:data-[panel-open=false]:translate-x-4'
      : smoothMotion
        ? 'data-[panel-display-style=sidebar]:data-[panel-open=false]:motion-safe:-translate-x-4'
        : 'data-[panel-display-style=sidebar]:data-[panel-open=false]:-translate-x-4'
  )
  const asideVariantClasses =
    variant === 'inset'
      ? cn(
          'data-[panel-display-style=sidebar]:bg-card',
          !expandPanel && [
            'data-[panel-display-style=sidebar]:my-3',
            'data-[panel-display-style=sidebar]:rounded-lg',
            'data-[panel-display-style=sidebar]:border',
            'data-[panel-display-style=sidebar]:shadow-sm',
            side === SidePanelSide.Right
              ? 'data-[panel-display-style=sidebar]:mr-3'
              : 'data-[panel-display-style=sidebar]:ml-3',
          ]
        )
      : undefined
  return cn(
    'relative flex min-w-0 min-h-0 flex-col overflow-visible bg-background',
    asideSheetClasses,
    asideSidebarClasses,
    asideVariantClasses,
    'data-[panel-display-style=sheet]:z-50',
    'data-[panel-modal=true]:z-50',
    'data-[panel-display-style=sheet]:data-[panel-open=false]:hidden'
  )
}

export function sidePanelContentClassName({
  expandPanel = false,
  variant,
}: {
  expandPanel?: boolean
  variant: 'flush' | 'inset'
}): string {
  return cn(
    'min-h-0 min-w-0 flex-1 overflow-y-auto',
    variant === 'inset' && !expandPanel && 'rounded-lg'
  )
}
